<script>
import { mapGetters } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const FALLBACK_VALUE = '—';

export default {
  components: {
    BaseSettingsHeader,
  },
  setup() {
    const { accountId } = useAccount();
    return {
      accountId,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
    }),
    locale() {
      return this.$i18n?.locale || navigator.language || 'en-US';
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
    usageSource() {
      return this.currentAccount.limits || {};
    },
    customAttributes() {
      return this.currentAccount.custom_attributes || {};
    },
    subscription() {
      return this.currentAccount.subscription || {};
    },
    seatUsage() {
      const used =
        this.usageSource.seats?.used ??
        this.currentAccount.agents_count ??
        this.currentAccount.users_count ??
        null;
      const limit =
        this.usageSource.seats?.limit ??
        this.subscription.subscribed_quantity ??
        null;
      return this.buildUsageBlock({
        icon: 'users',
        title: this.$t('USAGE_SETTINGS.CARDS.SEATS.TITLE'),
        used,
        limit,
        fallbackLimit: this.$t('USAGE_SETTINGS.UNLIMITED'),
        hint: this.$t('USAGE_SETTINGS.CARDS.SEATS.HINT'),
      });
    },
    inboxUsage() {
      const used =
        this.usageSource.inboxes?.used ??
        this.currentAccount.inboxes_count ??
        this.currentAccount.inboxes?.length ??
        null;
      const limit = this.usageSource.inboxes?.limit ?? null;
      return this.buildUsageBlock({
        icon: 'mailbox',
        title: this.$t('USAGE_SETTINGS.CARDS.INBOXES.TITLE'),
        used,
        limit,
        fallbackLimit: this.$t('USAGE_SETTINGS.UNLIMITED'),
        hint: this.$t('USAGE_SETTINGS.CARDS.INBOXES.HINT'),
      });
    },
    aiUsage() {
      const used =
        this.usageSource.ai_replies?.used ??
        this.customAttributes.ai_replies_used ??
        null;
      const limit =
        this.usageSource.ai_replies?.limit ??
        this.subscription.plan?.max_ai_replies_per_month ??
        null;

      return this.buildUsageBlock({
        icon: 'sparkles',
        title: this.$t('USAGE_SETTINGS.CARDS.AI.TITLE'),
        used,
        limit,
        fallbackLimit: this.$t('USAGE_SETTINGS.UNLIMITED'),
        hint: this.$t('USAGE_SETTINGS.CARDS.AI.HINT'),
      });
    },
    dataUsage() {
      const used = this.usageSource.storage?.used ?? null;
      const limit = this.usageSource.storage?.limit ?? null;
      return this.buildUsageBlock({
        icon: 'database',
        title: this.$t('USAGE_SETTINGS.CARDS.STORAGE.TITLE'),
        used,
        limit,
        fallbackLimit: this.$t('USAGE_SETTINGS.UNLIMITED'),
        hint: this.$t('USAGE_SETTINGS.CARDS.STORAGE.HINT'),
      });
    },
    usageCards() {
      return [this.seatUsage, this.inboxUsage, this.aiUsage, this.dataUsage];
    },
    planLimitRows() {
      const limits = this.currentAccount.plan_limits || [];
      if (!limits.length) {
        return [];
      }
      return limits.map(limit => ({
        key: limit?.key || 'limit',
        field: limit?.field,
        value: limit?.value,
        unlimited: !!limit?.unlimited,
        unit: limit?.unit,
      }));
    },
    recommendationCards() {
      return [
        {
          icon: 'trending-up',
          title: this.$t('USAGE_SETTINGS.RECOMMENDATIONS.MONITOR_GROWTH.TITLE'),
          description: this.$t(
            'USAGE_SETTINGS.RECOMMENDATIONS.MONITOR_GROWTH.DESCRIPTION'
          ),
        },
        {
          icon: 'shield-check',
          title: this.$t(
            'USAGE_SETTINGS.RECOMMENDATIONS.ASSIGN_ROLES.TITLE'
          ),
          description: this.$t(
            'USAGE_SETTINGS.RECOMMENDATIONS.ASSIGN_ROLES.DESCRIPTION'
          ),
        },
        {
          icon: 'bot',
          title: this.$t('USAGE_SETTINGS.RECOMMENDATIONS.EXPLORE_AI.TITLE'),
          description: this.$t(
            'USAGE_SETTINGS.RECOMMENDATIONS.EXPLORE_AI.DESCRIPTION'
          ),
        },
      ];
    },
  },
  mounted() {
    this.$store.dispatch('accounts/limits').catch(() => {});
  },
  methods: {
    buildUsageBlock({
      icon,
      title,
      used,
      limit,
      fallbackLimit = FALLBACK_VALUE,
      hint,
    }) {
      const normalizedUsed =
        used === null || used === undefined ? 0 : Number(used);
      const numericUsed = Number.isFinite(normalizedUsed)
        ? normalizedUsed
        : null;
      const numericLimit =
        typeof limit === 'number' && Number.isFinite(limit) ? limit : null;
      const progress =
        numericLimit && numericLimit > 0 && numericUsed !== null
          ? Math.min(100, Math.round((numericUsed / numericLimit) * 100))
          : null;

      return {
        icon,
        title,
        used:
          numericUsed !== null
            ? this.formatNumber(numericUsed)
            : FALLBACK_VALUE,
        limit:
          limit === null || limit === undefined
            ? fallbackLimit
            : typeof limit === 'number' && Number.isFinite(limit)
              ? this.formatNumber(limit)
              : limit,
        progress,
        hint,
      };
    },
    formatNumber(value) {
      if (value === null || value === undefined) {
        return FALLBACK_VALUE;
      }
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return value;
      }
      return numeric.toLocaleString(this.locale);
    },
  },
};
</script>

<template>
  <div>
    <woot-loading-state v-if="uiFlags.isFetchingItem" />
    <div v-else class="space-y-6">
      <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <BaseSettingsHeader
          :description="$t('USAGE_SETTINGS.PAGE_DESCRIPTION')"
          feature-name="usage"
        />
        <div class="flex flex-wrap gap-2">
          <woot-button
            variant="smooth"
            @click="$router.push({ name: 'billing_settings_index', params: { accountId } })"
          >
            {{ $t('USAGE_SETTINGS.CTA.GO_TO_BILLING') }}
          </woot-button>
        </div>
      </div>

      <section class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <article
          v-for="card in usageCards"
          :key="card.title"
          class="usage-card flex flex-col gap-3"
        >
          <div class="flex items-center justify-between">
            <div class="rounded-lg bg-hilab/10 p-2 text-hilab">
              <lucide-icon :name="card.icon" :size="18" />
            </div>
            <span
              v-if="card.progress !== null"
              class="text-xs font-medium text-slate-500 dark:text-slate-400"
            >
              {{ card.progress }}%
            </span>
          </div>
          <div>
            <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {{ card.title }}
            </h3>
            <p class="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
              {{ card.used }}
              <span class="text-base font-normal text-slate-500 dark:text-slate-400">
                / {{ card.limit }}
              </span>
            </p>
          </div>
          <p class="text-xs text-slate-500 dark:text-slate-400">
            {{ card.hint }}
          </p>
        </article>
      </section>

      <section class="usage-card space-y-4">
        <header class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {{ $t('USAGE_SETTINGS.PLAN_LIMITS.TITLE') }}
            </h2>
            <p class="text-sm text-slate-500 dark:text-slate-400">
              {{ $t('USAGE_SETTINGS.PLAN_LIMITS.DESCRIPTION') }}
            </p>
          </div>
          <woot-button
            variant="smooth"
            @click="$router.push({ name: 'settings_upgrade_index', params: { accountId } })"
          >
            {{ $t('USAGE_SETTINGS.PLAN_LIMITS.CTA') }}
          </woot-button>
        </header>
        <div
          v-if="planLimitRows.length"
          class="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700"
        >
          <table class="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead class="bg-slate-50 dark:bg-slate-800/80">
              <tr>
                <th class="table-head">
                  {{ $t('USAGE_SETTINGS.PLAN_LIMITS.LIMIT') }}
                </th>
                <th class="table-head">
                  {{ $t('USAGE_SETTINGS.PLAN_LIMITS.VALUE') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 bg-white text-sm dark:divide-slate-800 dark:bg-slate-900/40">
              <tr v-for="limit in planLimitRows" :key="limit.key">
                <td class="px-4 py-3 text-slate-700 dark:text-slate-200">
                  {{
                    $t(
                      `USAGE_SETTINGS.PLAN_LIMITS.LABELS.${limit.key.toUpperCase()}`,
                      limit.key.toUpperCase()
                    )
                  }}
                </td>
                <td class="px-4 py-3 text-slate-600 dark:text-slate-300">
                  <template v-if="limit.unlimited">
                    {{ $t('USAGE_SETTINGS.UNLIMITED') }}
                  </template>
                  <template v-else>
                    {{ formatNumber(limit.value) }}
                    <span v-if="limit.unit">
                      {{ $t(`USAGE_SETTINGS.PLAN_LIMITS.UNITS.${limit.unit.toUpperCase()}`) }}
                    </span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-else
          class="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300"
        >
          {{ $t('USAGE_SETTINGS.PLAN_LIMITS.EMPTY') }}
        </div>
      </section>

      <section class="grid gap-4 md:grid-cols-3">
        <article
          v-for="card in recommendationCards"
          :key="card.title"
          class="usage-card flex flex-col gap-3"
        >
          <div class="rounded-lg bg-slate-900/5 p-2 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100">
            <lucide-icon :name="card.icon" :size="18" />
          </div>
          <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {{ card.title }}
          </h3>
          <p class="text-sm text-slate-500 dark:text-slate-300">
            {{ card.description }}
          </p>
        </article>
      </section>
    </div>
  </div>
</template>

<style lang="scss">
.usage-card {
  @apply rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800;
  transition: none !important;
  transform: none !important;
  cursor: default;
}

.usage-card:hover {
  transform: none !important;
  box-shadow: inherit;
}

.table-head {
  @apply px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400;
}
</style>
